import { Fragment, PropsWithChildren, ReactNode, useCallback, useEffect, useState } from 'react';
import {
  DatabaseUserDocumentType,
  User,
  UserAddress,
  UserDocument,
  UserDocumentType,
  UserDriverLic,
  UserInternalNote,
} from '../../../clients/quipli-service/user/user';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { QuipliLink } from '../../../components/link/quipli-link';
import { Typography } from '../../../components/typography/typography';
import { OrderSection } from '../order-section/order-section';
import { Spinner } from '../../../components/spinner/spinner';
import { UserAddressForm } from '../../customer/components/user-address-form';
import { UserForm } from '../../customer/components/user-form';
import { Radio } from '../../../components/radio/radio';
import { Collapsible } from '../../../components/collapsible/collapsible';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { FileField } from '../../../components/field/field';
import { toast } from '../../../util/toasts';
import { Badge } from '../../../components/badge/badge';
import { TextArea } from '../../../components/textarea/textarea';
import { useAdmin } from '../../../hooks/use-admin-context';
import { LicenseScanModal, UserDocumentForm } from '../../customer/components/user-document-form';
import classNames from 'classnames';
import { CustomerRepresentative } from './customer-representative';
import { useInvoiceApiV1 } from '../../../clients/quipli-service/invoice/invoice-api-v1';
import { InsuranceItem } from '../../customer/components/document-item';
import { InsuranceDocument } from '../../../clients/customer-api/customer/customer-customer';
import Dinero from 'dinero.js';
import { formatPhoneNumber } from '../../../util/util';
import { Representative } from '../../../clients/quipli-service/representatives/representative';
import { Vendor } from '../../../clients/quipli-service/vendor/vendor';
import { NotificationType } from '../../../clients/quipli-service/notfication/notification';
import { useCompatibleDownload } from '../../../hooks/use-compatible-download';
import { DateTime } from 'luxon';

export const CustomerInfo = ({
  customer,
  setCustomer,
  billing,
  delivery,
  representative,
  setBilling,
  setDelivery,
  setRepresentative,
  isCreateMode = false,
  isEditMode = false,
  orderId,
  setBalanceAmount,
}: {
  customer: User;
  setCustomer?: (c: User | null) => void;
  billing?: UserAddress;
  delivery?: UserAddress;
  representative?: Representative;
  setBilling: (a?: UserAddress) => void;
  setDelivery: (a?: UserAddress) => void;
  setRepresentative: (r?: Representative) => void;
  isCreateMode?: boolean;
  isEditMode?: boolean;
  orderId?: number;
  setBalanceAmount?: (a: Dinero.Dinero) => void;
}) => {
  const { getUserAddresses, getConfiguration } = useAPI();
  const { getCustomerBalances } = useInvoiceApiV1();

  // States
  const [isCustomerModalOpen, openCustomerModal] = useState<boolean>(false);
  const [isAddressListLoading, setIsAddressListLoading] = useState<boolean>(false);
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [balance, setBalance] = useState<Dinero.Dinero>();
  const [showCreditLimit, setShowCreditLimit] = useState<boolean>(false);
  const [unsavedAddresses, setUnsavedAddresses] = useState<UserAddress[]>([]);

  // Callbacks
  const getAddressList = useCallback(async () => {
    setIsAddressListLoading(true);
    let userAddresses = await getUserAddresses(customer.id, true);

    // To ensure the selected address still appears when editing
    const selectedAddress1s = [billing?.address1, delivery?.address1];

    // Filter out deleted, unless its been added in current session (in the unsavedAddresses obj)
    if (unsavedAddresses.length > 0) {
      userAddresses = userAddresses.filter(
        (a) =>
          a.isDeleted === false ||
          unsavedAddresses.some((ua) => ua.address1 === a.address1 && ua.zip === a.zip) ||
          selectedAddress1s.includes(a.address1),
      );
    } else {
      userAddresses = userAddresses.filter((a) => a.isDeleted === false || selectedAddress1s.includes(a.address1));
    }

    setAddresses(userAddresses);
    setIsAddressListLoading(false);
  }, [customer, unsavedAddresses]);

  const getBalance = async () => {
    const config = await getConfiguration('CONF_CREDIT_LIMIT');
    const creditLimitEnabled = config?.value === '1';
    if (creditLimitEnabled && setBalanceAmount && customer.creditLimit && customer.creditLimit?.getAmount()) {
      setShowCreditLimit(true);
      const customerBalances = await getCustomerBalances([customer.id]);
      const customerBalance = Dinero({
        amount: (customerBalances.find((cb) => cb.customerId == customer.id)?.totalDue || 0) * 100,
        currency: customer.creditLimit?.getCurrency(),
      });
      setBalance(customerBalance);
      setBalanceAmount && setBalanceAmount(customerBalance);
    }
  };

  const initialize = async () => {
    await getAddressList();
    await getBalance();
  };

  useEffect(() => {
    initialize();
  }, [customer, unsavedAddresses]);

  return (
    <>
      <div className="flex flex-col gap-y-[12px]">
        <OrderSection
          className="flex flex-col gap-[8px]"
          header={
            <div className="flex justify-between items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Customer
              </Typography>
              <div>
                <Dropdown
                  trigger={() => (
                    <Button className="button-tertiary">
                      <Icon icon="DotsHorizontal" />
                    </Button>
                  )}
                >
                  <div onClick={() => openCustomerModal(true)}>
                    <Typography type="text-sm" className="text-quipli-black-800">
                      Edit customer details
                    </Typography>
                  </div>

                  {isCreateMode && (
                    <div onClick={() => setCustomer?.(null)}>
                      <Typography type="text-sm" className="text-error-400">
                        Remove customer
                      </Typography>
                    </div>
                  )}
                </Dropdown>
              </div>
            </div>
          }
        >
          <QuipliLink
            target="_blank"
            to={`users/customer_form/${customer.id}`}
            className="text-link-200 underline w-fit"
          >
            <Typography type="text-sm">{customer.name}</Typography>
          </QuipliLink>

          {customer.doNoRent && (
            <Typography type="text-sm" className="text-error-500">
              Customer has DNR flag!
            </Typography>
          )}

          {customer.paymentBan && (
            <Typography type="text-sm" className="text-error-500">
              Customer has a payment ban!
            </Typography>
          )}

          <div className="border-t border-solid border-x-0 border-b-0 border-t-grey-200 pt-[12px]">
            <Typography type="text-md" weight="medium" className="text-grey-800">
              Contact Information
            </Typography>

            <div className="pt-[8px] flex flex-col gap-y-[8px]">
              <QuipliLink
                target="_blank"
                to={`users/customer_form/${customer.id}`}
                className="text-link-200 underline w-fit"
              >
                <Typography type="text-sm">{customer.email}</Typography>
              </QuipliLink>
              <Typography type="text-sm" className="text-grey-800">
                {formatPhoneNumber(customer.phone)}
              </Typography>
            </div>
          </div>

          {showCreditLimit && (
            <div className="border-t border-solid border-x-0 border-b-0 border-t-grey-200 pt-[12px]">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Credit Utilization
              </Typography>

              <div className="pt-[8px] flex flex-col gap-y-[8px]">
                <Typography type="text-sm" className="text-grey-800">
                  {`${balance?.toFormat() ?? 0} used of ${customer.creditLimit?.toFormat()}`}
                </Typography>
              </div>
            </div>
          )}

          <CustomerAddressList
            customer={customer}
            type="billing"
            isLoading={isAddressListLoading}
            setUnsavedAddresses={setUnsavedAddresses}
            list={addresses.filter((a) => a.type === 'billing')}
            fetchList={getAddressList}
            address={billing}
            setAddress={setBilling}
            isEditing={isEditMode}
          />
          <CustomerAddressList
            customer={customer}
            type="delivery"
            isLoading={isAddressListLoading}
            setUnsavedAddresses={setUnsavedAddresses}
            list={addresses.filter((a) => a.type === 'delivery')}
            fetchList={getAddressList}
            address={delivery}
            setAddress={setDelivery}
            isEditing={isEditMode}
          />
          <CustomerRepresentative
            customer={customer}
            representative={representative}
            setRepresentative={setRepresentative}
            isCreateMode={isCreateMode}
            isOrderEditing={isEditMode}
            orderId={orderId}
          />
        </OrderSection>

        <CustomerDocuments customer={customer} setCustomer={setCustomer} orderId={orderId} />
        <CustomerNotes customer={customer} />
      </div>

      {/* Modals */}
      <UserForm
        isOpen={isCustomerModalOpen}
        user={customer}
        setNewUser={setCustomer}
        close={() => openCustomerModal(false)}
      />
    </>
  );
};

const CustomerAddressModal = ({
  address,
  close,
  hideAddressType,
  hideSaveForFutureUse,
  setUnsavedAddresses,
}: {
  address: UserAddress;
  close: () => void;
  hideAddressType?: boolean;
  hideSaveForFutureUse?: boolean;
  setUnsavedAddresses: any;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  useEffect(() => {
    !isOpen && close();
  }, [isOpen]);

  return (
    <UserAddressForm
      setUnsavedAddresses={setUnsavedAddresses}
      isOpen={isOpen}
      onSubmit={() => setIsOpen(false)}
      onClose={() => setIsOpen(false)}
      userAddress={address}
      hideAddressType={hideAddressType}
      hideSaveForFutureUse={hideSaveForFutureUse}
    />
  );
};

export const CustomerAddressList = ({
  customer,
  title,
  type,
  isLoading,
  list,
  fetchList,
  address,
  setAddress,
  isEditing = false,
  canAdd = true,
  canSelect = true,
  hideAddressType,
  hideSaveForFutureUse,
  setUnsavedAddresses,
}: {
  customer: User | Vendor;
  title?: string;
  type: 'billing' | 'delivery';
  isLoading: boolean;
  list: UserAddress[];
  fetchList: () => Promise<void>;
  address?: UserAddress;
  setAddress: (a?: UserAddress) => void;
  isEditing?: boolean;
  canAdd?: boolean;
  canSelect?: boolean;
  hideAddressType?: boolean;
  hideSaveForFutureUse?: boolean;
  setUnsavedAddresses: any;
}) => {
  const { deleteUserAddress } = useAPI();

  const [selectedAddress, setSelectedAddress] = useState<UserAddress>();
  const [addressModal, setAddressModal] = useState<ReactNode>();

  const defaultAddress = {
    id: 0,
    name: customer.name,
    userId: customer.id,
    phone: '',
    companyName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    type: type,
    isGoogleGenerated: false,
  } as UserAddress;

  const removeAddress = async (addressId: number) => {
    try {
      await deleteUserAddress(addressId);
      await fetchList();
      toast('Address removed successfully!');
    } catch (error) {
      toast('Something went wrong!', 'error');
    }
  };

  useEffect(() => {
    let selected = address || undefined;

    if (isEditing) {
      selected = selected || list.find((a) => a.isDefault) || list[0];
    }

    if (selected === selectedAddress) return;

    setSelectedAddress(selected);
    setAddress(selected);
  }, [address, list]);

  return (
    <>
      <div className="border-t border-solid border-x-0 border-b-0 border-t-grey-200 pt-[12px]">
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="flex items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                {title || `${type === 'billing' ? 'Billing ' : 'Delivery'} Address`}
              </Typography>

              {isEditing && canAdd && (
                <div>
                  <Button
                    className="button-tertiary"
                    onClick={(e) => {
                      e.preventDefault();
                      setAddressModal(
                        <CustomerAddressModal
                          address={defaultAddress}
                          hideAddressType={hideAddressType}
                          hideSaveForFutureUse={hideSaveForFutureUse}
                          setUnsavedAddresses={setUnsavedAddresses}
                          close={() => {
                            fetchList();
                            setAddressModal(undefined);
                          }}
                        />,
                      );
                    }}
                  >
                    <Icon icon="Plus" />
                  </Button>
                </div>
              )}
            </div>

            {isEditing ? (
              <div className="pt-[8px] flex flex-col gap-y-[8px] max-h-[200px] overflow-auto">
                {list.length > 0 ? (
                  list.map((_address, index) => (
                    <div
                      key={`${type}-user-address-${index}`}
                      className="flex items-start justify-between gap-x-[20px]"
                    >
                      <div className="flex items-start gap-x-[10px] mt-[10px]">
                        {canSelect && (
                          <div>
                            <Radio
                              name={`selected-${type}-address-id`}
                              isChecked={_address.id === address?.id}
                              onChange={(isChecked) => isChecked && setAddress(_address)}
                            />
                          </div>
                        )}
                        <div>
                          <Typography type="text-sm" className="text-grey-800 whitespace-pre-line">
                            {`${_address.name}\n`}
                            {_address.companyName && `${_address.companyName}\n`}
                            {_address.address1}
                            {!_address.isGoogleGenerated &&
                              `${_address.address2 ? `\n${_address.address2}` : ''}${
                                _address.city ? `\n${_address.city}` : ''
                              }${_address.state ? `, ${_address.state}` : ''}${
                                _address.zip ? `\n ${_address.zip}` : ''
                              }`}
                          </Typography>
                        </div>
                      </div>

                      <div>
                        <Dropdown
                          trigger={() => (
                            <Button className="button-tertiary">
                              <Icon icon="DotsHorizontal" />
                            </Button>
                          )}
                        >
                          <div
                            onClick={(e) => {
                              e.preventDefault();
                              setAddressModal(
                                <CustomerAddressModal
                                  address={_address}
                                  hideAddressType={hideAddressType}
                                  hideSaveForFutureUse={hideSaveForFutureUse}
                                  setUnsavedAddresses={setUnsavedAddresses}
                                  close={() => {
                                    fetchList();
                                    setAddressModal(undefined);
                                  }}
                                />,
                              );
                            }}
                          >
                            <Typography type="text-sm" className="text-quipli-black-800">
                              Edit address
                            </Typography>
                          </div>

                          <div onClick={() => removeAddress(_address.id)}>
                            <Typography type="text-sm" className="text-error-400">
                              Remove address
                            </Typography>
                          </div>
                        </Dropdown>
                      </div>
                    </div>
                  ))
                ) : (
                  <Button
                    className="button-secondary w-fit"
                    onClick={(e) => {
                      e.preventDefault();
                      setAddressModal(
                        <CustomerAddressModal
                          address={defaultAddress}
                          hideAddressType={hideAddressType}
                          hideSaveForFutureUse={hideSaveForFutureUse}
                          setUnsavedAddresses={setUnsavedAddresses}
                          close={() => {
                            fetchList();
                            setAddressModal(undefined);
                          }}
                        />,
                      );
                    }}
                  >
                    Add address
                  </Button>
                )}
              </div>
            ) : (
              <div>
                <Typography type="text-sm" className="text-grey-800 whitespace-pre-line">
                  {selectedAddress ? (
                    <>
                      {`${selectedAddress.name}\n`}
                      {selectedAddress.companyName && `${selectedAddress.companyName}\n`}
                      {selectedAddress.address1}
                      {!selectedAddress.isGoogleGenerated &&
                        `${selectedAddress.address2 ? `\n${selectedAddress.address2}` : ''}${
                          selectedAddress.city ? `\n${selectedAddress.city}` : ''
                        }${selectedAddress.state ? `, ${selectedAddress.state}` : ''}${
                          selectedAddress.zip ? `\n ${selectedAddress.zip}` : ''
                        }`}
                    </>
                  ) : (
                    <>-</>
                  )}
                </Typography>
              </div>
            )}
          </>
        )}
      </div>

      {addressModal}
    </>
  );
};

const CustomerNotes = ({ customer }: { customer: User }) => {
  const { listUserInternalNotes, createUserInternalNote } = useAPI();
  const admin = useAdmin();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customerNotes, setCustomerNotes] = useState<UserInternalNote[]>([]);

  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const [note, setNote] = useState<string>('');

  const submit = async () => {
    if (isSubmitLoading || note.length === 0) return;

    setIsSubmitLoading(true);

    try {
      const body = {
        userId: customer.id,
        comment: note,
        authorId: admin.id,
        edited: false,
      };
      const res = await createUserInternalNote(customer.id, body);
      setCustomerNotes((prev) => [...prev, res]);

      setNote('');

      toast('Customer note added sucessfully');
    } catch (ex: any) {
      toast(ex.message.msg || ex.message, 'error');
    }

    setIsSubmitLoading(false);
  };

  useEffect(() => {
    if (!customer) return;

    const getCustomerNotes = async () => {
      setIsLoading(true);
      setCustomerNotes(await listUserInternalNotes(customer.id));

      setIsLoading(false);
    };

    getCustomerNotes();
  }, [customer]);

  return (
    <>
      <OrderSection
        className="flex flex-col gap-[8px]"
        header={
          <div className="flex items-center">
            <div className="flex items-center w-full justify-between gap-x-[4px]">
              <div className="flex items-center gap-x-[4px]">
                <Typography type="text-md" weight="medium" className="text-grey-800">
                  Customer Notes
                </Typography>

                {customerNotes.length > 3 && (
                  <QuipliLink target="_blank" to={`users/customer_form/${customer.id}`} className="cursor-pointer">
                    <Badge text={`+${customerNotes.length - 3}`} type="info" size="small" />
                  </QuipliLink>
                )}
              </div>
            </div>
          </div>
        }
      >
        <div className="bg-teal flex flex-col gap-[8px]">
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              {customerNotes.slice(-3).map((note, index) => (
                <div
                  key={`user-${customer.id}-note-${index}`}
                  className="rounded-[8px] border border-solid border-grey-200 p-[14px]"
                >
                  <Typography type="text-sm" className="text-quipli-black-800 whitespace-pre-line">
                    {note.comment}
                  </Typography>
                </div>
              ))}

              <TextArea
                className="w-full h-fit bg-grey-50 text-sm"
                placeholder="Add a customer note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />

              {isSubmitLoading ? (
                <Spinner />
              ) : (
                note.length > 0 && (
                  <div className="button-group">
                    <Button className="button-secondary" onClick={submit}>
                      Add Note
                    </Button>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </OrderSection>
    </>
  );
};

export const CustomerDocuments = ({
  customer,
  setCustomer,
  className,
  orderId,
}: {
  customer: User;
  setCustomer?: (user: User) => void;
  className?: string;
  orderId?: number;
}) => {
  const { getMultipleConfigurations, getLabel, getUserDocuments, getUserDriverLicense, getInsuranceDocuments } =
    useAPI();

  const [conf, setConf] = useState<Configuration[]>([]);
  const [driverLicense, setDriverLicense] = useState<UserDriverLic>();
  const [insurance, setInsurance] = useState<InsuranceDocument>();
  const [documentsToShow, setDocumentsToShow] = useState<
    { key: string; label: string; file?: UserDocument; type: DatabaseUserDocumentType; isRequired: boolean }[]
  >([]);

  const loadCustomerDocuments = async () => {
    const [label, documents, driverLicense, insurance] = await Promise.all([
      getLabel('LBL_CUSTOM_DOC_UPLOAD'),
      getUserDocuments(customer.id),
      getUserDriverLicense(customer.id),
      conf.find((c) => c.variable === 'CONF_PROOF_OF_INSURANCE')?.value === '1'
        ? getInsuranceDocuments(customer.id)
        : Promise.resolve(undefined),
    ]);

    const _docsToShow: {
      key: string;
      label: string;
      file?: UserDocument;
      type: DatabaseUserDocumentType;
      isRequired: boolean;
    }[] = [];
    setDriverLicense(driverLicense);

    const latestDriverLicense = documents
      .sort(
        (a, b) =>
          DateTime.fromISO(b.createdAt, { setZone: true }).toMillis() -
          DateTime.fromISO(a.createdAt, { setZone: true }).toMillis(),
      )
      .find((d) => d.documentType === UserDocumentType.License);
    _docsToShow.push({
      key: 'CONF_RENTER_LIC_INFO',
      label: 'Renter Information',
      file: latestDriverLicense,
      type: DatabaseUserDocumentType.License,
      isRequired: true,
    });

    if (insurance && insurance.length > 0) {
      setInsurance(insurance[0]);
    }
    _docsToShow.push({
      key: 'CONF_PROOF_OF_INSURANCE',
      label: 'Proof of Insurance',
      file: documents.find((d) => d.documentType === UserDocumentType.Insurance),
      type: DatabaseUserDocumentType.Insurance,
      isRequired: conf.find((c) => c.variable === 'CONF_PROOF_INSURANCE_MANDATORY')?.value === '1',
    });

    if (conf.find((c) => c.variable === 'CONF_OTHER_DOC_UPLOAD')?.value === '1') {
      _docsToShow.push({
        key: 'CONF_OTHER_DOC_UPLOAD',
        label: label?.caption || 'Other Document',
        file: documents.find((d) => d.documentType === UserDocumentType.OtherCheckout),
        type: DatabaseUserDocumentType.OtherCheckout,
        isRequired: conf.find((c) => c.variable === 'CONF_OTHER_DOC_REQUIRED')?.value === '1',
      });
    }

    setDocumentsToShow(_docsToShow);
  };

  useEffect(() => {
    const getConf = async () => {
      const configuration = await getMultipleConfigurations([
        'CONF_RENTER_LIC_INFO',
        'CONF_PROOF_OF_INSURANCE',
        'CONF_PROOF_INSURANCE_MANDATORY',
        'CONF_OTHER_DOC_UPLOAD',
        'CONF_OTHER_DOC_REQUIRED',
      ]);

      setConf(configuration);
    };

    getConf();
  }, []);

  useEffect(() => {
    if (conf.length === 0) return;

    loadCustomerDocuments();
  }, [conf, customer]);

  return (
    <>
      {documentsToShow.length > 0 && (
        <OrderSection className={classNames(className)}>
          {documentsToShow.map((doc, idx) => (
            <Fragment key={`customer-doc-${idx}`}>
              {idx > 0 && <div className="border-0 border-b border-solid border-grey-200 my-[10px]"></div>}
              <CustomerDocumentForm
                customerId={customer.id}
                label={doc.label}
                documentType={doc.type}
                reload={loadCustomerDocuments}
                file={doc.file}
                isUploaded={
                  !!doc.file ||
                  (doc.type === DatabaseUserDocumentType.License && !!driverLicense) ||
                  (doc.type === DatabaseUserDocumentType.Insurance && !!insurance)
                }
              >
                {doc.type === DatabaseUserDocumentType.License && (
                  <CustomerDriverLicenseForm
                    customer={customer}
                    setCustomer={setCustomer}
                    driverLicense={driverLicense}
                    reload={loadCustomerDocuments}
                    orderId={orderId}
                  />
                )}

                {doc.type === DatabaseUserDocumentType.Insurance && (
                  <CustomerInsuranceForm customer={customer} insurance={insurance} reload={loadCustomerDocuments} />
                )}
              </CustomerDocumentForm>
            </Fragment>
          ))}
        </OrderSection>
      )}
    </>
  );
};

const CustomerDocumentForm = ({
  customerId,
  label,
  documentType,
  reload,
  file,
  isUploaded,
  children,
}: PropsWithChildren<{
  customerId: number;
  label: string;
  documentType: DatabaseUserDocumentType;
  reload: () => void;
  file?: UserDocument;
  isUploaded: boolean;
}>) => {
  const { createUpdateUserDocument, downloadUserDocumentFile } = useAPI();
  const { download } = useCompatibleDownload();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const uploadDocument = async (inputFile: File) => {
    if (!inputFile) return;

    setIsLoading(true);

    try {
      await createUpdateUserDocument(inputFile, `${customerId}`, `${documentType}`);
      toast('Document uploaded successfully!');
      setIsLoading(false);
      reload();
    } catch (error) {
      toast('Something went wrong!', 'error');
      setIsLoading(false);
    }
  };

  const downloadUserFile = async (userId: number, fileId: number) => {
    try {
      await download(() => downloadUserDocumentFile(userId, fileId));
    } catch (error: any) {
      toast('Something went wrong.', 'error');
    }
  };

  return (
    <Collapsible
      trigger={(isOpen) => (
        <div className="flex justify-between items-center cursor-pointer">
          <div className="flex gap-x-[8px] items-center">
            <Icon
              icon={isUploaded ? 'CheckCircle' : 'InfoCircle'}
              stroke={isUploaded ? 'var(--success-500)' : undefined}
            />
            <Typography type="text-md" weight="medium" className="text-grey-800">
              {label}
            </Typography>
          </div>

          <Button className="button-tertiary">
            <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
          </Button>
        </div>
      )}
    >
      <div className="pt-[12px] pb-[18px] flex flex-col gap-y-[12px] w-full">
        {!isLoading ? (
          <FileField
            showIcon={false}
            value={undefined}
            onChange={(newInputFile) => !!newInputFile && uploadDocument(newInputFile as File)}
          />
        ) : (
          <div className="min-h-[100px] flex items-center justify-center">
            <Spinner />
          </div>
        )}
        {!!file && (
          <div
            className="w-full text-grey-400 flex gap-x-[10px] items-center justify-between"
            onClick={() => downloadUserFile(file.userId, file.id)}
          >
            <div className="shrink break-words w-[calc(100%_-_52px_-_10px)]">
              <Typography type="text-sm" className="">
                {file.fileName}
              </Typography>
            </div>

            <Icon icon="Download"></Icon>
          </div>
        )}
      </div>

      {children}
    </Collapsible>
  );
};

const CustomerDriverLicenseForm = ({
  customer,
  setCustomer,
  driverLicense,
  reload,
  orderId,
}: {
  customer: User;
  setCustomer?: (c: User) => void;
  driverLicense?: UserDriverLic;
  reload: () => void;
  orderId?: number;
}) => {
  const { getMultipleConfigurations, sendNotification } = useAPI();

  const hasManualLicense = !!driverLicense;
  const [hasHardwareLicenseScanning, setHasHardwareLicenseScanning] = useState<boolean>(false);
  const [hasMobileLicenseScanning, setHasMobileLicenseScanning] = useState<boolean>(false);
  const [licenseScanProfileVersion, setLicenseScanProfileVersion] = useState<number>(1);
  const [modal, setModal] = useState<ReactNode>();

  useEffect(() => {
    getMultipleConfigurations([
      'CONF_ENABLE_HARDWARE_LICENSE_SCANNING',
      'CONF_IDSCAN_LICENSE_KEY',
      'CONF_IDSCAN_PROFILE_VERSION',
    ]).then((conf) => {
      setHasHardwareLicenseScanning(
        conf.find((c) => c.variable === 'CONF_ENABLE_HARDWARE_LICENSE_SCANNING')?.value === '1',
      );
      setHasMobileLicenseScanning(conf.find((c) => c.variable === 'CONF_IDSCAN_LICENSE_KEY') !== undefined);

      const idScanProfileVersion = conf.find((c) => c.variable === 'CONF_IDSCAN_PROFILE_VERSION')?.value || '1';
      setLicenseScanProfileVersion(parseInt(idScanProfileVersion));
    });
  }, []);

  return (
    <>
      {hasManualLicense && (
        <div className="flex flex-col gap-[8px]">
          <div className="flex flex-row gap-[4px] justify-between items-center">
            <Typography type="text-sm" weight="medium" className="text-grey-500">
              Full Name
            </Typography>

            <Typography type="text-sm" className="text-grey-500">
              {driverLicense.fullName}
            </Typography>
          </div>

          <div className="flex flex-row gap-[4px] justify-between items-center">
            <Typography type="text-sm" weight="medium" className="text-grey-500">
              Date of Birth
            </Typography>

            <Typography type="text-sm" className="text-grey-500">
              {driverLicense.dateOfBirth ? driverLicense.dateOfBirth.toFormat('LL/dd/yyyy') : '-'}
            </Typography>
          </div>

          <div className="flex flex-row gap-[4px] justify-between items-center">
            <Typography type="text-sm" weight="medium" className="text-grey-500">
              License Number
            </Typography>

            <Typography type="text-sm" className="text-grey-500">
              {driverLicense.driverLicNum}
            </Typography>
          </div>

          <div className="flex flex-row gap-[4px] justify-between items-center">
            <Typography type="text-sm" weight="medium" className="text-grey-500">
              Issuing State/Province
            </Typography>

            <Typography type="text-sm" className="text-grey-500">
              {driverLicense.state ? driverLicense.state.name : '-'}
            </Typography>
          </div>

          <div className="flex flex-row gap-[4px] justify-between items-center">
            <Typography type="text-sm" weight="medium" className="text-grey-500">
              Expiration Date
            </Typography>

            <Typography type="text-sm" className="text-grey-500">
              {driverLicense.expirationDate.toFormat('LL/dd/yyyy')}
            </Typography>
          </div>
        </div>
      )}

      <Button
        className={classNames('button-secondary float-right', {
          'mt-6': hasManualLicense,
        })}
        onClick={(e) => {
          e.preventDefault();
          setModal(
            <UserDocumentForm
              isOpen={true}
              setIsOpen={() => setModal(undefined)}
              isEdit={hasManualLicense}
              user={customer}
              userDriverLicense={driverLicense}
              onSubmit={reload}
              onMergeUsers={(mergedUser) => setCustomer?.(mergedUser)}
              hideDocumentType
              hideFile
              existingFile={{ documentType: UserDocumentType.License }}
            />,
          );
        }}
      >
        Edit manually
      </Button>

      {hasHardwareLicenseScanning && (
        <Button
          className={classNames('button-secondary float-right mr-2', {
            'mt-6': hasManualLicense,
          })}
          onClick={(e) => {
            e.preventDefault();
            setModal(
              <LicenseScanModal
                isOpen={true}
                setIsOpen={() => setModal(undefined)}
                user={customer}
                onSuccess={reload}
                licenseScanProfileVersion={licenseScanProfileVersion}
              />,
            );
          }}
        >
          Scan License
        </Button>
      )}

      {hasMobileLicenseScanning && orderId && (
        <Button
          className={classNames('button-secondary float-right mr-2', {
            'mt-6': hasManualLicense,
          })}
          onClick={(e) => {
            e.preventDefault();
            sendNotification({
              type: NotificationType.scanLicense,
              userId: customer.id,
              format: 'sms',
              orderId: orderId,
            })
              .then(() => {
                toast('License scan SMS sent successfully!');
              })
              .catch(() => {
                toast('Failed to send license scan SMS', 'error');
              });
          }}
        >
          Resend License Scan SMS
        </Button>
      )}

      {modal}
    </>
  );
};

const CustomerInsuranceForm = ({
  insurance,
  customer,
  reload,
}: {
  insurance: InsuranceDocument | undefined;
  customer: User;
  reload: () => void;
}) => {
  const [modal, setModal] = useState<ReactNode>();
  return (
    <>
      {insurance && <InsuranceItem insurance={insurance} />}
      <Button
        id="edit-insurance-button"
        className={classNames('button-secondary float-right', {
          'mt-6': !!insurance,
        })}
        onClick={(e) => {
          e.preventDefault();
          setModal(
            <UserDocumentForm
              isOpen={true}
              setIsOpen={() => setModal(undefined)}
              isEdit={!!insurance}
              user={customer}
              onSubmit={reload}
              hideDocumentType
              hideFile
              existingFile={{ documentType: UserDocumentType.Insurance }}
              insuranceDocument={insurance}
            />,
          );
        }}
      >
        Edit manually
      </Button>

      {modal}
    </>
  );
};
